import { Pressable, Text, useDripsyTheme, View } from 'dripsy';
import * as React from 'react';
import { Platform } from 'react-native';
import { useActive, useFocus, useHover } from 'react-native-web-hooks';
const TEST_ID = 'doppio-button';
const webStyles = {
    display: 'inline-flex',
    transition: 'outline-offset 0.15s ease',
};
const Button = React.forwardRef(({ accessibilityHint, accessibilityLabel, children, fullWidth = false, iconLeft, iconRight, id, isDisabled = false, onPressIn, onPressOut, size = 'default', variant = 'primary', ...props }, forwardedRef) => {
    const ref = React.useRef(forwardedRef);
    const [isPressed, setIsPressed] = React.useState(false);
    const isHovered = useHover(ref);
    const isActive = useActive(ref);
    const isFocusVisible = useFocus(ref);
    const { theme } = useDripsyTheme();
    const buttons = theme.buttons;
    const shouldRenderHover = !isDisabled && isHovered;
    const shouldRenderActive = !isDisabled && (isActive || isPressed);
    const shouldRenderFocus = !isDisabled && (isFocusVisible || isPressed);
    const isIconButton = Boolean(!children && (iconLeft || iconRight));
    const variantSx = buttons[variant];
    const hoverColor = 'color' in variantSx.$$hover ? variantSx.$$hover.color : variantSx.color;
    const activeColor = 'color' in variantSx.$$active ? variantSx.$$active.color : variantSx.color;
    const disabledColor = 'color' in variantSx.$$disabled ? variantSx.$$disabled.color : variantSx.color;
    const mapSizeToPaddingVertical = {
        small: '$1',
        big: '$3',
    };
    const mapSizeToPaddingHorizontal = {
        small: '$3',
        big: '$4',
    };
    const mapSizeToHeight = {
        small: '$7',
        big: '$10',
    };
    const paddingVertical = (size && mapSizeToPaddingVertical[size]) ?? '$2';
    const paddingHorizontal = (size && mapSizeToPaddingHorizontal[size]) ?? '$4';
    const height = (size && mapSizeToHeight[size]) ?? '$8';
    const pressInHandler = React.useCallback((event) => {
        setIsPressed(true);
        if (onPressIn)
            onPressIn(event);
    }, [onPressIn]);
    const pressOutHandler = React.useCallback((event) => {
        setIsPressed(false);
        if (onPressOut)
            onPressOut(event);
    }, [onPressOut]);
    return (React.createElement(Pressable, { sx: {
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'row',
            borderWidth: 1,
            paddingTop: paddingVertical,
            paddingRight: paddingHorizontal,
            paddingBottom: paddingVertical,
            paddingLeft: paddingHorizontal,
            height,
            outlineStyle: 'solid',
            outlineWidth: 0,
            outlineOffset: 0,
            ...(shouldRenderFocus && {
                outlineWidth: 1,
                outlineOffset: 3,
            }),
            ...(fullWidth && { width: '100%' }),
            ...(isIconButton && { width: height }),
            ...(Platform.OS === 'web' && !fullWidth && webStyles),
            ...(shouldRenderHover && variantSx.$$hover),
            ...(shouldRenderActive && variantSx.$$active),
            ...(isDisabled && {
                cursor: 'not-allowed',
                ...variantSx.$$disabled,
            }),
        }, themeKey: "buttons", variant: variant, ref: ref, disabled: isDisabled, accessibilityHint: accessibilityHint, accessibilityLabel: accessibilityLabel, accessibilityRole: "button", accessibilityState: { disabled: isDisabled }, onPressIn: pressInHandler, onPressOut: pressOutHandler, testID: TEST_ID, nativeID: id, ...props },
        iconLeft && (React.createElement(View, { sx: {
                ...(!isIconButton && { paddingRight: '$4' }),
            } }, React.cloneElement(iconLeft, {
            color: variantSx.color,
            ...(shouldRenderHover && { color: hoverColor }),
            ...(shouldRenderActive && {
                color: activeColor,
            }),
            ...(isDisabled && { color: disabledColor }),
        }))),
        children && (React.createElement(Text, { sx: {
                color: variantSx.color,
                fontWeight: 'fontWeight' in variantSx ? variantSx.fontWeight : 'normal',
                userSelect: 'none',
                ...(shouldRenderHover && { color: hoverColor }),
                ...(shouldRenderActive && { color: activeColor }),
                ...(isDisabled && { color: disabledColor }),
            } }, children)),
        iconRight && (React.createElement(View, { sx: {
                ...(!isIconButton && { paddingLeft: '$4' }),
            } }, React.cloneElement(iconRight, {
            ...('color' in variantSx && { color: variantSx.color }),
            ...(shouldRenderHover && { color: hoverColor }),
            ...(shouldRenderActive && { color: activeColor }),
            ...(isDisabled && { color: disabledColor }),
        })))));
});
const ButtonCompound = Object.assign({}, Button, { displayName: 'Button', testID: TEST_ID });
export { ButtonCompound as Button };
//# sourceMappingURL=Button.js.map